/**
 * QuickActions - Enterprise Admin Quick Actions
 * Azioni rapide per amministratori del sistema
 */

import React, { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useServers } from '@/hooks/useServers'
import { useMetrics } from '@/hooks/useMetrics'
import { apiFetchJson, API_ENDPOINTS } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { 
  RefreshCw, 
  Search, 
  Settings, 
  BarChart3,
  Shield,
  Zap,
  Download
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

export const QuickActions: React.FC = () => {
  const navigate = useNavigate()
  const { discoverServers, refresh: refreshServers, refreshCache } = useServers()
  const { refresh: refreshMetrics } = useMetrics()
  const [loading, setLoading] = useState<string | null>(null)

  const executeAction = async (
    actionId: string,
    action: () => Promise<any>,
    successMessage: string | ((result: any) => string),
  ) => {
    setLoading(actionId)
    try {
      const result = await action()
      toast.success(typeof successMessage === 'function' ? successMessage(result) : successMessage)
    } catch (error) {
      toast.error(`Azione fallita: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`)
    } finally {
      setLoading(null)
    }
  }

  const actions = useMemo(() => ([
    {
      id: 'refresh-servers',
      title: 'Aggiorna server',
      description: 'Aggiorna lo stato dei server',
      icon: <RefreshCw className="h-4 w-4" />,
      action: () => refreshServers(),
      variant: 'default' as const,
      shortcut: 'R'
    },
    {
      id: 'discover-servers',
      title: 'Discovery Docker',
      description: 'Elenca i container Docker rilevati',
      icon: <Search className="h-4 w-4" />,
      action: async () => {
        const res = await discoverServers()
        const count = Number(res?.containers_found ?? 0)
        return { ...res, count }
      },
      variant: 'secondary' as const,
      shortcut: 'D'
    },
    {
      id: 'refresh-cache',
      title: 'Aggiorna cache discovery',
      description: 'Invalida cache e ricarica i server',
      icon: <RefreshCw className="h-4 w-4" />,
      action: () => refreshCache(),
      variant: 'outline' as const,
      shortcut: 'C'
    },
    {
      id: 'refresh-metrics',
      title: 'Aggiorna metriche',
      description: 'Aggiorna le metriche di sistema',
      icon: <BarChart3 className="h-4 w-4" />,
      action: () => refreshMetrics(),
      variant: 'outline' as const,
      shortcut: 'M'
    },
    {
      id: 'system-check',
      title: 'Controllo sistema',
      description: 'Diagnostica health',
      icon: <Shield className="h-4 w-4" />,
      action: async () => {
        const [health, db, bot] = await Promise.all([
          apiFetchJson<any>(API_ENDPOINTS.HEALTH),
          apiFetchJson<any>(API_ENDPOINTS.DATABASE_HEALTH),
          apiFetchJson<any>(API_ENDPOINTS.BOT_HEALTH),
        ])
        const backendOk = String(health?.status || '').toLowerCase() === 'ok'
        const dbOk = Boolean(db?.postgresql?.ok) && Boolean(db?.redis?.ok)
        const botOk = Boolean(bot?.healthy)
        if (!backendOk || !dbOk || !botOk) {
          const parts = [
            `backend:${backendOk ? 'ok' : 'fail'}`,
            `db:${dbOk ? 'ok' : 'fail'}`,
            `bot:${botOk ? 'ok' : 'fail'}`,
          ]
          throw new Error(parts.join(' '))
        }
        return { backendOk, dbOk, botOk }
      },
      variant: 'outline' as const,
      shortcut: 'H'
    }
  ]), [discoverServers, refreshCache, refreshMetrics, refreshServers])

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Zap className="h-4 w-4" />
          Azioni rapide
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <div className="grid grid-cols-1 gap-3">
          {actions.map((action, index) => (
            <motion.div
              key={action.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Button
                variant={action.variant}
                size="sm"
                className="w-full justify-start h-auto p-3"
                onClick={() => executeAction(action.id, action.action, (result) => {
                  if (action.id === 'discover-servers') {
                    return `Discovery Docker: ${Number(result?.containers_found ?? result?.count ?? 0)} container`
                  }
                  if (action.id === 'refresh-cache') {
                    return `Cache discovery aggiornata: ${Number(result?.servers_found ?? 0)} server`
                  }
                  if (action.id === 'system-check') return 'Controllo sistema: tutto operativo'
                  return `${action.title} completata`
                })}
                disabled={loading === action.id}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted">
                    {loading === action.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      action.icon
                    )}
                  </div>
                  
                  <div className="flex-1 text-left">
                    <div className="font-medium text-sm">{action.title}</div>
                    <div className="text-xs text-muted-foreground">{action.description}</div>
                  </div>
                  
                  <Badge variant="outline" className="text-xs">
                    {action.shortcut}
                  </Badge>
                </div>
              </Button>
            </motion.div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t">
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => navigate('/admin/settings')}
            >
              <Settings className="h-3 w-3 mr-1" />
              Impostazioni
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => executeAction(
                'export-settings',
                async () => {
                  const data = await apiFetchJson<any>(API_ENDPOINTS.ADMIN_SETTINGS_ALL)
                  const json = JSON.stringify(data, null, 2)
                  const blob = new Blob([json], { type: 'application/json' })
                  const url = URL.createObjectURL(blob)
                  try {
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `ummr-settings-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
                    a.click()
                  } finally {
                    URL.revokeObjectURL(url)
                  }
                  return { ok: true }
                },
                'Impostazioni esportate'
              )}
              disabled={loading === 'export-settings'}
            >
              <Download className="h-3 w-3 mr-1" />
              Esporta
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
